import type {
  CSSObject,
  CSSSelectorObject,
  SystemCssProperties,
} from '@styled-system/css';
import css from '@styled-system/css';
import type * as CSS from 'csstype';
import type { CSSProp, DefaultTheme } from 'styled-components';
import styled from 'styled-components';
import type { BaseObject, ExecutionProps } from 'styled-components/dist/types';
import type {
  ColorProps,
  FlexboxProps,
  LayoutProps,
  ResponsiveValue,
  SpaceProps,
  styleFn,
} from 'styled-system';
import { color, compose, flexbox, layout, space } from 'styled-system';

import createCustomParser from './createCustomParser';
import shouldForwardProp from './shouldForwardProp';

/**
 * Adds transition CSS properties.
 */
const transition = createCustomParser({
  transition: true,
  transitionProperty: true,
  transitionDelay: true,
  transitionDuration: {
    property: 'transitionDuration',
    scale: 'transitionDuration',
  },
  transitionTimingFunction: {
    property: 'transitionTimingFunction',
    scale: 'transitionTimingFunction',
  },
});

export interface TransitionProps {
  transition?: ResponsiveValue<CSS.Property.Transition>;
  transitionProperty?: ResponsiveValue<CSS.Property.TransitionProperty>;
  transitionDelay?: ResponsiveValue<CSS.Property.TransitionDelay>;
  transitionDuration?: ResponsiveValue<CSS.Property.TransitionDuration>;
  transitionTimingFunction?: ResponsiveValue<CSS.Property.TransitionTimingFunction>;
}

/**
 * Adds visibility CSS property.
 */
const visibility = createCustomParser({
  visibility: true,
});

export interface VisibilityProps {
  visibility?: ResponsiveValue<CSS.Property.Visibility>;
}

/**
 * Adds aspect-ratio CSS property.
 */
const aspectRatio = createCustomParser({
  aspectRatio: true,
});

export interface AspectRatioProps {
  aspectRatio?: ResponsiveValue<CSS.Property.AspectRatio>;
}

/**
 * Adds color-scheme CSS property.
 */
const colorScheme = createCustomParser({
  colorScheme: true,
});

export interface ColorSchemeProps {
  colorScheme?: ResponsiveValue<CSS.Property.ColorScheme>;
}

/**
 * The sx prop is like the css prop in styled-components but with an
 * object literal syntax and access to theme values.
 *
 * Example:
 * <Box sx={{ borderColor: 'primary'}}/>
 */
export interface SxProps {
  sx?: CSSSelectorObject | SystemCssProperties;
}

const sx = (props: SxProps & { theme?: DefaultTheme }): CSSObject =>
  css(props.sx)(props.theme);

export interface BaseStyledProps
  extends AspectRatioProps,
    ColorSchemeProps,
    TransitionProps,
    ColorProps,
    FlexboxProps,
    LayoutProps,
    SpaceProps,
    SxProps,
    VisibilityProps,
    ExecutionProps {
  css?: CSSProp;
}

function mergeDefaultPropsAndProps(
  defaultProps: BaseObject | undefined,
  props: BaseObject | undefined,
) {
  if (defaultProps == null) {
    return props ?? {};
  }
  if (props == null) {
    return defaultProps;
  }
  // Note:
  // 1. omitting className because styled-components seems to add it elsewhere
  // so if it's included we'll get a class attribute on the element with the
  // className repeated twice. TODO - remove?
  // 2. omitting undefined values to not overwrite values in defaultProps.

  const filteredProps = Object.fromEntries(
    Object.entries(props).filter(
      ([key, value]) => key !== 'className' && value !== undefined,
    ),
  );
  return { ...defaultProps, ...filteredProps };
}

/**
 * Returns a Styled Components function enhanced with Styled
 * System props.
 */
const styledFactory = <
  Props extends BaseObject,
  Target extends
    | keyof React.JSX.IntrinsicElements
    | React.JSXElementConstructor<Props>,
>(
  /**
   * Either a valid React component or a tagname like 'div'.
   */
  target: Target,
  /**
   * For easier debugging and react dev tools inspection
   */
  displayName: string,
  /**
   *  Optional array of Styled System variant parsers
   */
  variantParsers?: styleFn[],
  /**
   * Optional array of custom Styled System system parsers
   * that will add additional styling props to the component
   */
  extensionParsers?: styleFn[],
  /**
   * Optional css string to apply to the component's styles
   */
  css?: CSSProp,
  /**
   * Optional default props to apply to the component
   */
  defaultProps?: Partial<
    BaseStyledProps & Props & React.ComponentProps<Target>
  >,
) => {
  // Note: variantParsers are composed first so that other parsers takes precendence.
  // This allows consumers to override styles from variants.
  const Component = styled(target)
    .withConfig({ shouldForwardProp })
    .attrs(props => mergeDefaultPropsAndProps(defaultProps, props))<
    BaseStyledProps & Props
  >`
    min-width: 0;
    ${css}
    ${compose(...(variantParsers || []))} 
    ${sx}
    ${compose(
      aspectRatio,
      colorScheme,
      transition,
      color,
      flexbox,
      layout,
      space,
      visibility,
      ...(extensionParsers || []),
    )}
  `;
  Component.displayName = displayName;
  return Component;
};

export default styledFactory;
